// A WebSocket to the game server (/ws) that reconnects with exponential backoff.
// Authentication is the session cookie; close codes 4001 (opened in another window) and 4401 (signed out)
// are final.

export type Fatal = 'replaced' | 'signed_out';

export class ServerSocket {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  /** connected (again): send the subscription / join message here */
  onOpen: () => void = () => {};
  onMessage: (m: any) => void = () => {};
  /** the connection dropped; it is being re-established */
  onOffline: () => void = () => {};
  onFatal: (why: Fatal) => void = () => {};

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
  }

  get online(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(m: unknown): boolean {
    if (!this.online) return false;
    this.ws!.send(JSON.stringify(m));
    return true;
  }

  private open(): void {
    const ws = new WebSocket((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws');
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.onOpen();
    };
    ws.onmessage = (ev) => {
      let m: any;
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.onMessage(m);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws || this.stopped) return;
      this.ws = null;
      if (ev.code === 4001) return this.fatal('replaced');
      if (ev.code === 4401) return this.fatal('signed_out');
      this.onOffline();
      void this.retry();
    };
  }

  private fatal(why: Fatal): void {
    this.stopped = true;
    this.onFatal(why);
  }

  private async retry(): Promise<void> {
    this.attempts++;
    // a refused handshake looks like any network error: after a few failures ask whether we are signed out
    if (this.attempts >= 3) {
      try {
        const r = await fetch('/api/me', { credentials: 'same-origin' });
        if (r.status === 401) return this.fatal('signed_out');
      } catch {
        /* server unreachable: keep trying */
      }
    }
    const delay = Math.min(15000, 500 * 2 ** Math.min(this.attempts, 5)) * (0.75 + Math.random() * 0.5);
    this.timer = setTimeout(() => !this.stopped && this.open(), delay);
  }
}
